import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import {
  ChevronDown, ChevronRight, Users, FileText, BarChart3,
  TrendingUp, Calculator, Activity
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────
//  Types
// ─────────────────────────────────────────────────────────────────────

interface SelectedProperty {
  id: number;
  propertyName: string;
  propertyType: string;
  location: string;
}

interface Props {
  property: SelectedProperty | null;
  onClose: () => void;
}

interface Summary {
  rentRoll?: any;
  latestKpi?: any;
  noiTrendCount?: number;
  hasArgus?: boolean;
  hasJv?: boolean;
}

// ─────────────────────────────────────────────────────────────────────
//  Styled
// ─────────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(139, 92, 246, 0.25);
  border-radius: 16px;
  padding: 1.25rem 1.5rem 1.5rem;
  margin-top: 1.25rem;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  padding-bottom: 0.75rem;
  margin-bottom: 1rem;
`;

const HeaderTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Sub = styled.div`
  font-size: 0.75rem;
  color: #94a3b8;
  letter-spacing: 0.04em;
  margin-top: 0.15rem;
`;

const CloseBtn = styled.button`
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #f87171;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.35rem 0.7rem;
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: rgba(239, 68, 68, 0.18); }
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const SummaryTile = styled.div`
  background: rgba(30, 41, 59, 0.55);
  border: 1px solid rgba(255,255,255,0.06);
  border-left: 3px solid rgba(139,92,246,0.7);
  border-radius: 8px;
  padding: 0.6rem 0.85rem;
`;

const TileLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 0.2rem;
`;

const TileValue = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: #fff;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
`;

const Accordion = styled.div`
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 10px;
  margin-bottom: 0.6rem;
  overflow: hidden;
  background: rgba(15, 23, 42, 0.4);
`;

const AccHead = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.7rem 0.9rem;
  background: rgba(30, 41, 59, 0.5);
  border: none;
  cursor: pointer;
  color: #e2e8f0;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-align: left;
  &:hover { background: rgba(51, 65, 85, 0.5); }
`;

const AccTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

const Pill = styled.span<{ $variant?: 'amber'|'red'|'emerald'|'slate' }>`
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 2px 7px;
  border-radius: 999px;
  text-transform: uppercase;
  ${p => p.$variant === 'amber'   && `background: rgba(245,158,11,0.12); color: #fbbf24; border:1px solid rgba(245,158,11,0.4);`}
  ${p => p.$variant === 'red'     && `background: rgba(239,68,68,0.12);  color: #f87171; border:1px solid rgba(239,68,68,0.4);`}
  ${p => p.$variant === 'emerald' && `background: rgba(16,185,129,0.12); color: #34d399; border:1px solid rgba(16,185,129,0.4);`}
  ${p => (!p.$variant || p.$variant === 'slate') && `background: rgba(100,116,139,0.12); color: #94a3b8; border:1px solid rgba(100,116,139,0.4);`}
`;

const AccBody = styled.div`
  padding: 0.85rem 1rem;
  font-size: 0.78rem;
  color: #cbd5e1;
  border-top: 1px solid rgba(255,255,255,0.05);
  background: rgba(2, 6, 23, 0.35);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.72rem;
  th, td {
    text-align: left;
    padding: 0.4rem 0.55rem;
    border-bottom: 1px solid rgba(255,255,255,0.04);
  }
  th {
    color: #64748b;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.62rem;
  }
  td {
    color: #cbd5e1;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
  }
  td.num { text-align: right; }
`;

const Empty = styled.div`
  padding: 1rem;
  text-align: center;
  color: #64748b;
  font-style: italic;
  font-size: 0.78rem;
`;

const Spin = styled.div`
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  &::before {
    content: '';
    width: 14px; height: 14px;
    border: 2px solid rgba(139, 92, 246, 0.2);
    border-top-color: #a78bfa;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

// ─────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────

const fmtMoney = (v: any) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};
const fmtPct = (v: any, mult = 1) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${(n * mult).toFixed(2)}%`;
};
const fmtDate = (v: any) => (v ? String(v).slice(0, 10) : '—');
const fmtNum = (v: any, dp = 2) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: dp });
};

// ─────────────────────────────────────────────────────────────────────
//  Component
// ─────────────────────────────────────────────────────────────────────

const RealEstateOperationsPanel: React.FC<Props> = ({ property, onClose }) => {
  // open state per accordion
  const [open, setOpen] = useState<Record<string, boolean>>({
    rentRoll: true, leases: false, kpi: false, noi: false, argus: false, jv: false
  });

  const [summary, setSummary] = useState<Summary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // section data — lazy-loaded on first open
  const [rentRoll, setRentRoll] = useState<any[] | null>(null);
  const [leases, setLeases] = useState<any[] | null>(null);
  const [kpi, setKpi] = useState<any[] | null>(null);
  const [noiTrend, setNoiTrend] = useState<any[] | null>(null);
  const [argus, setArgus] = useState<any[] | null>(null);
  const [jvTerms, setJvTerms] = useState<any[] | null>(null);

  const [loading, setLoading] = useState<Record<string, boolean>>({});

  const propertyId = property?.id;

  // reset when property changes
  useEffect(() => {
    setRentRoll(null);
    setLeases(null);
    setKpi(null);
    setNoiTrend(null);
    setArgus(null);
    setJvTerms(null);
    setSummary(null);
    if (!propertyId) return;

    setSummaryLoading(true);
    axios.get(`/api/credit/re-equity/${propertyId}/operations/summary`)
      .then(r => setSummary(r.data || {}))
      .catch(() => setSummary({}))
      .finally(() => setSummaryLoading(false));
  }, [propertyId]);

  const ensureLoaded = async (section: string) => {
    if (!propertyId) return;
    const need =
      (section === 'rentRoll' && rentRoll === null) ||
      (section === 'leases'   && leases === null) ||
      (section === 'kpi'      && kpi === null) ||
      (section === 'noi'      && noiTrend === null) ||
      (section === 'argus'    && argus === null) ||
      (section === 'jv'       && jvTerms === null);
    if (!need) return;

    setLoading(prev => ({ ...prev, [section]: true }));
    try {
      if (section === 'rentRoll') {
        const r = await axios.get(`/api/credit/re-equity/${propertyId}/rent-roll`);
        setRentRoll(Array.isArray(r.data) ? r.data : []);
      } else if (section === 'leases') {
        const r = await axios.get(`/api/credit/re-equity/${propertyId}/leases`);
        setLeases(Array.isArray(r.data) ? r.data : []);
      } else if (section === 'kpi') {
        const r = await axios.get(`/api/credit/re-equity/${propertyId}/operating-kpi`);
        setKpi(Array.isArray(r.data) ? r.data : []);
      } else if (section === 'noi') {
        const r = await axios.get(`/api/credit/re-equity/${propertyId}/noi-trend`);
        setNoiTrend(Array.isArray(r.data) ? r.data : []);
      } else if (section === 'argus') {
        const r = await axios.get(`/api/credit/re-equity/${propertyId}/argus`);
        setArgus(Array.isArray(r.data) ? r.data : []);
      } else if (section === 'jv') {
        const r = await axios.get(`/api/credit/re-equity/${propertyId}/jv-terms`);
        setJvTerms(Array.isArray(r.data) ? r.data : []);
      }
    } catch (e) {
      console.error(`Failed to load ${section}`, e);
    } finally {
      setLoading(prev => ({ ...prev, [section]: false }));
    }
  };

  const toggle = (key: string) => {
    setOpen(prev => {
      const next = { ...prev, [key]: !prev[key] };
      if (next[key]) ensureLoaded(key);
      return next;
    });
  };

  // pre-fetch the first section eagerly when property changes
  useEffect(() => {
    if (propertyId && open.rentRoll && rentRoll === null) ensureLoaded('rentRoll');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  // ─── summary tiles ───
  const tiles = useMemo(() => {
    if (!summary) return [];
    const rr = summary.rentRoll || {};
    const k = summary.latestKpi || {};
    return [
      { label: 'Rent Roll Units', value: rr.unitCount != null ? fmtNum(rr.unitCount, 0) : '—' },
      { label: 'Occupancy', value: rr.occupancyPct != null ? `${fmtNum(rr.occupancyPct, 1)}%` : '—' },
      { label: 'In-Place Rent', value: rr.inPlaceRent != null ? fmtMoney(rr.inPlaceRent) : '—' },
      { label: 'Latest NOI', value: k.noi != null ? fmtMoney(k.noi) : '—' },
      { label: 'Period', value: k.periodType ? `${k.periodType} ${k.periodEnd ? '· ' + fmtDate(k.periodEnd) : ''}` : '—' },
      { label: 'NOI History', value: summary.noiTrendCount != null ? `${summary.noiTrendCount} pts` : '—' }
    ];
  }, [summary]);

  if (!property) return null;

  return (
    <Wrap>
      <Header>
        <div>
          <HeaderTitle>
            <Activity className="w-4 h-4 text-violet-400" />
            Operations · {property.propertyName}
          </HeaderTitle>
          <Sub>{property.propertyType} · {property.location}</Sub>
        </div>
        <CloseBtn onClick={onClose}>Close</CloseBtn>
      </Header>

      {/* Summary tiles */}
      {summaryLoading ? <Spin /> : (
        <SummaryRow>
          {tiles.map((t, i) => (
            <SummaryTile key={i}>
              <TileLabel>{t.label}</TileLabel>
              <TileValue>{t.value}</TileValue>
            </SummaryTile>
          ))}
        </SummaryRow>
      )}

      {/* Rent Roll */}
      <Accordion>
        <AccHead onClick={() => toggle('rentRoll')}>
          <AccTitle>
            {open.rentRoll ? <ChevronDown className="w-4 h-4 text-violet-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            <Users className="w-4 h-4 text-violet-400" />
            Rent Roll
            {summary?.rentRoll?.unitCount != null && (
              <Pill $variant="slate">{summary.rentRoll.unitCount} units</Pill>
            )}
          </AccTitle>
        </AccHead>
        {open.rentRoll && (
          <AccBody>
            {loading.rentRoll ? <Spin />
              : (rentRoll && rentRoll.length > 0) ? (
                <Table>
                  <thead>
                    <tr>
                      <th>Unit</th><th>Tenant</th><th>Type</th>
                      <th className="num">SF</th><th className="num">Mo Rent</th>
                      <th>Lease End</th><th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rentRoll.slice(0, 60).map((u: any, i: number) => (
                      <tr key={u.id ?? i}>
                        <td>{u.unitNumber || u.unit_number || '—'}</td>
                        <td>{u.tenantName || u.tenant_name || (u.vacant === true ? 'Vacant' : '—')}</td>
                        <td>{u.unitType || u.unit_type || '—'}</td>
                        <td className="num">{fmtNum(u.squareFeet ?? u.square_feet, 0)}</td>
                        <td className="num">{fmtMoney(u.monthlyRent ?? u.monthly_rent)}</td>
                        <td>{fmtDate(u.leaseEndDate ?? u.lease_end_date)}</td>
                        <td>{u.vacant === true ? <Pill $variant="red">Vacant</Pill> : <Pill $variant="emerald">Occupied</Pill>}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : <Empty>No rent-roll data ingested for this property yet.</Empty>
            }
          </AccBody>
        )}
      </Accordion>

      {/* Leases */}
      <Accordion>
        <AccHead onClick={() => toggle('leases')}>
          <AccTitle>
            {open.leases ? <ChevronDown className="w-4 h-4 text-violet-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            <FileText className="w-4 h-4 text-blue-400" />
            Leases &amp; Lease-Roll
          </AccTitle>
        </AccHead>
        {open.leases && (
          <AccBody>
            {loading.leases ? <Spin />
              : (leases && leases.length > 0) ? (
                <Table>
                  <thead>
                    <tr>
                      <th>Tenant</th><th>Unit</th><th>Start</th><th>End</th>
                      <th className="num">Base Rent</th><th>Type</th><th>Renewal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leases.slice(0, 60).map((l: any, i: number) => (
                      <tr key={l.id ?? i}>
                        <td>{l.tenantName || l.tenant_name || '—'}</td>
                        <td>{l.unitNumber || l.unit_number || '—'}</td>
                        <td>{fmtDate(l.leaseStartDate ?? l.lease_start_date)}</td>
                        <td>{fmtDate(l.leaseEndDate ?? l.lease_end_date)}</td>
                        <td className="num">{fmtMoney(l.baseRent ?? l.base_rent)}</td>
                        <td>{l.leaseType || l.lease_type || '—'}</td>
                        <td>{l.renewalOption || l.renewal_option || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : <Empty>No active leases on file.</Empty>
            }
          </AccBody>
        )}
      </Accordion>

      {/* Operating KPIs (T-12 / T-3) */}
      <Accordion>
        <AccHead onClick={() => toggle('kpi')}>
          <AccTitle>
            {open.kpi ? <ChevronDown className="w-4 h-4 text-violet-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            <BarChart3 className="w-4 h-4 text-emerald-400" />
            Operating KPIs (T-12 / T-3)
          </AccTitle>
        </AccHead>
        {open.kpi && (
          <AccBody>
            {loading.kpi ? <Spin />
              : (kpi && kpi.length > 0) ? (
                <Table>
                  <thead>
                    <tr>
                      <th>Type</th><th>Period End</th>
                      <th className="num">Revenue</th>
                      <th className="num">OpEx</th>
                      <th className="num">NOI</th>
                      <th className="num">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kpi.slice(0, 24).map((k: any, i: number) => {
                      const rev = Number(k.revenue ?? k.total_revenue ?? 0);
                      const opex = Number(k.operatingExpenses ?? k.operating_expenses ?? 0);
                      const noi = Number(k.noi ?? k.net_operating_income ?? 0);
                      const margin = rev > 0 ? (noi / rev) * 100 : 0;
                      return (
                        <tr key={k.id ?? i}>
                          <td><Pill $variant="slate">{k.periodType || k.period_type}</Pill></td>
                          <td>{fmtDate(k.periodEnd ?? k.period_end)}</td>
                          <td className="num">{fmtMoney(rev)}</td>
                          <td className="num">{fmtMoney(opex)}</td>
                          <td className="num" style={{ color: '#34d399', fontWeight: 700 }}>{fmtMoney(noi)}</td>
                          <td className="num">{margin > 0 ? `${margin.toFixed(1)}%` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              ) : <Empty>No T-12 / T-3 operating statements ingested yet.</Empty>
            }
          </AccBody>
        )}
      </Accordion>

      {/* NOI Trend */}
      <Accordion>
        <AccHead onClick={() => toggle('noi')}>
          <AccTitle>
            {open.noi ? <ChevronDown className="w-4 h-4 text-violet-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            NOI Trend
          </AccTitle>
        </AccHead>
        {open.noi && (
          <AccBody>
            {loading.noi ? <Spin />
              : (noiTrend && noiTrend.length > 0) ? (
                <Table>
                  <thead>
                    <tr>
                      <th>Period End</th><th>Type</th>
                      <th className="num">NOI</th><th className="num">Δ vs Prior</th>
                    </tr>
                  </thead>
                  <tbody>
                    {noiTrend.map((p: any, i: number) => {
                      const noi = Number(p.noi ?? p.net_operating_income ?? 0);
                      const prior = i + 1 < noiTrend.length ? Number(noiTrend[i + 1].noi ?? noiTrend[i + 1].net_operating_income ?? 0) : 0;
                      const delta = prior > 0 ? ((noi - prior) / prior) * 100 : 0;
                      const sign = delta >= 0 ? '+' : '';
                      return (
                        <tr key={p.id ?? i}>
                          <td>{fmtDate(p.periodEnd ?? p.period_end)}</td>
                          <td><Pill $variant="slate">{p.periodType || p.period_type}</Pill></td>
                          <td className="num">{fmtMoney(noi)}</td>
                          <td className="num" style={{ color: delta >= 0 ? '#34d399' : '#f87171' }}>
                            {prior > 0 ? `${sign}${delta.toFixed(1)}%` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              ) : <Empty>NOI trend appears once 2+ operating statements are on file.</Empty>
            }
          </AccBody>
        )}
      </Accordion>

      {/* Argus DCF */}
      <Accordion>
        <AccHead onClick={() => toggle('argus')}>
          <AccTitle>
            {open.argus ? <ChevronDown className="w-4 h-4 text-violet-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            <Calculator className="w-4 h-4 text-amber-400" />
            Argus DCF Projections
            {summary?.hasArgus && <Pill $variant="amber">Available</Pill>}
          </AccTitle>
        </AccHead>
        {open.argus && (
          <AccBody>
            {loading.argus ? <Spin />
              : (argus && argus.length > 0) ? (
                <Table>
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th className="num">PGI</th>
                      <th className="num">EGI</th>
                      <th className="num">OpEx</th>
                      <th className="num">NOI</th>
                      <th className="num">CapEx</th>
                      <th className="num">CF Before Debt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {argus.map((p: any, i: number) => (
                      <tr key={p.id ?? i}>
                        <td>{p.projectionYear ?? p.projection_year ?? '—'}</td>
                        <td className="num">{fmtMoney(p.potentialGrossIncome ?? p.potential_gross_income)}</td>
                        <td className="num">{fmtMoney(p.effectiveGrossIncome ?? p.effective_gross_income)}</td>
                        <td className="num">{fmtMoney(p.operatingExpenses ?? p.operating_expenses)}</td>
                        <td className="num" style={{ color: '#34d399', fontWeight: 700 }}>{fmtMoney(p.netOperatingIncome ?? p.net_operating_income)}</td>
                        <td className="num">{fmtMoney(p.capitalExpenditures ?? p.capital_expenditures)}</td>
                        <td className="num">{fmtMoney(p.cashFlowBeforeDebt ?? p.cash_flow_before_debt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : <Empty>No Argus DCF export ingested for this property.</Empty>
            }
          </AccBody>
        )}
      </Accordion>

      {/* JV Terms */}
      <Accordion>
        <AccHead onClick={() => toggle('jv')}>
          <AccTitle>
            {open.jv ? <ChevronDown className="w-4 h-4 text-violet-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            <Activity className="w-4 h-4 text-blue-400" />
            JV / Promote Terms
            {summary?.hasJv && <Pill $variant="emerald">On File</Pill>}
          </AccTitle>
        </AccHead>
        {open.jv && (
          <AccBody>
            {loading.jv ? <Spin />
              : (jvTerms && jvTerms.length > 0) ? (
                <div>
                  {jvTerms.map((j: any, i: number) => (
                    <div key={j.id ?? i} style={{ marginBottom: '0.8rem' }}>
                      <div style={{ fontWeight: 700, color: '#fff', marginBottom: '0.3rem' }}>
                        {(j.jvName || j.jv_name) ?? `JV ${i + 1}`}
                        {(j.gpName || j.gp_name) && <span style={{ color: '#94a3b8', fontWeight: 400 }}> · GP: {j.gpName || j.gp_name}</span>}
                      </div>
                      <Table>
                        <thead>
                          <tr>
                            <th>Tier</th>
                            <th className="num">Pref Return</th>
                            <th className="num">LP Split</th>
                            <th className="num">GP Split</th>
                            <th className="num">Catch-up</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(j.tiers || j.promote_tiers || []).map((t: any, ti: number) => (
                            <tr key={ti}>
                              <td>Tier {t.tier ?? ti + 1}</td>
                              <td className="num">{fmtPct(t.prefReturn ?? t.pref_return, 100)}</td>
                              <td className="num">{fmtPct(t.lpSplit ?? t.lp_split, 100)}</td>
                              <td className="num">{fmtPct(t.gpSplit ?? t.gp_split, 100)}</td>
                              <td className="num">{t.catchUp ?? t.catch_up ? 'Yes' : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      {j.notes && <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginTop: '0.3rem' }}>{j.notes}</div>}
                    </div>
                  ))}
                </div>
              ) : <Empty>No JV agreement ingested for this property.</Empty>
            }
          </AccBody>
        )}
      </Accordion>
    </Wrap>
  );
};

export default RealEstateOperationsPanel;
